/**
 * Client-side session end helpers.
 * OIDC access tokens expire (~1h); without handling, storage keeps a dead session
 * and the UI stays "logged in" while APIs/MQTT fail.
 */

import { clearStoredSessionKeys } from '@/core/auth/tokenStore';

export const SESSION_END_EVENT = 'scada:session-ended';

/** @returns {number|null} JWT exp claim in unix seconds, or null if not a JWT / unreadable */
export function readJwtExpSeconds(token) {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4));
    const json = JSON.parse(atob(b64 + pad));
    return typeof json.exp === 'number' ? json.exp : null;
  } catch {
    return null;
  }
}

/** True when JWT is past exp (with skew). Non-JWT tokens are never clock-expired. */
export function isAccessTokenExpired(token, skewSeconds = 30) {
  const exp = readJwtExpSeconds(token);
  if (exp == null) return false;
  return Date.now() / 1000 >= exp - skewSeconds;
}

export function clearStoredSession() {
  clearStoredSessionKeys();
}

export function loginPath() {
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  return `${base}/login` || '/login';
}

let ending = false;

/**
 * Clear stored session and notify the app once (deduped).
 * AuthProvider listens and hard-redirects to login when needed.
 */
export function endSession(reason = 'expired') {
  if (ending) return;
  ending = true;
  clearStoredSession();
  try {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(SESSION_END_EVENT, { detail: { reason } }));
    }
  } finally {
    setTimeout(() => {
      ending = false;
    }, 2500);
  }
}

export function onSessionEnd(handler) {
  if (typeof window === 'undefined') return () => {};
  const listener = (event) => handler(event?.detail || {});
  window.addEventListener(SESSION_END_EVENT, listener);
  return () => window.removeEventListener(SESSION_END_EVENT, listener);
}

/** Hard navigation so hooks/sockets cannot keep a zombie session. */
export function redirectToLogin() {
  if (typeof window === 'undefined') return;
  const path = loginPath();
  const current = window.location.pathname || '';
  if (current === path || current.endsWith('/login')) return;
  window.location.replace(path);
}
